// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useState } from 'react';
import { Modal } from '@loa/ui-components';

type MaxFundingSaveScenarioModalProps = Readonly<{
  open: boolean;
  defaultName?: string;
  saving: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (name: string) => void;
  title?: string;
  primaryActionLabel?: string;
  headerIcon?: string;
}>;

export function MaxFundingSaveScenarioModal({
  open,
  defaultName,
  saving,
  error,
  onClose,
  onSave,
  title = 'Save This Scenario',
  primaryActionLabel = 'Save Scenario',
  headerIcon,
}: MaxFundingSaveScenarioModalProps) {
  const [name, setName] = useState(defaultName ?? '');

  useEffect(() => {
    if (open) {
      setName(defaultName ?? '');
    }
  }, [open, defaultName]);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !saving;

  const handleSave = () => {
    if (!canSave) return;
    onSave(trimmed);
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="medium">
      <Modal.Header icon={headerIcon} title={title} onClose={saving ? undefined : handleClose} />
      <Modal.Content>
        <div
          className="flex flex-col gap-sm"
          data-testid="save-scenario-modal"
        >
          <label
            htmlFor="save-scenario-name-input"
            className="text-body-02 font-normal text-black font-sans"
          >
            Scenario Name
          </label>
          <input
            id="save-scenario-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            maxLength={120}
            className="h-[48px] px-md rounded-lg border border-black bg-white text-body-02 text-black font-sans focus:outline-none focus:border-purple-primary"
            data-testid="save-scenario-name-input"
            autoFocus
          />
          {error && (
            <span className="text-body-03 text-error-text" role="alert">{error}</span>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{
          label: 'Cancel',
          onClick: handleClose,
          disabled: saving,
        }}
        primaryAction={{
          label: primaryActionLabel,
          onClick: handleSave,
          loading: saving,
          disabled: !canSave,
        }}
      />
    </Modal>
  );
}
